import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ignoreSessionUnavailable } from '@ais-ui/auth';
import {
  getTargetList,
  TargetFromTargetList,
  useSession,
} from '@ais-ui/websockets';
import {
  getDimensionsFromMap,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { useMapOverlay } from './contexts/MapOverlayContext';
import { useMapPanelContext } from './MapPanel/MapPanelContext';
import { CENTERCOMPONENTS } from './MapPanel/CenterComponents';
import { useAddShiplist } from '@ais-ui/api';
import { shiplistCallbacks } from '../utils/mutateCallbacks';
import { buildShiplistFromTargets } from '../utils/buildShiplistFromTargets';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useFilterContext } from './FilterContext';
import { MainWindowActions, DataType, isTargetsPopupActive } from '../Popup';

export const BULK_TRACKS_MAX = 50;

interface TargetsOnMapContextProps {
  targets: TargetFromTargetList[];
  onGetTargets: (
    requestedPerPage?: number | null,
  ) => Promise<TargetFromTargetList[]>;
  total: number;
  perPage: number;
  setPerPage: Dispatch<SetStateAction<number>>;
  retrievalTime: string;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isLoading: boolean;
  isSubmitting: boolean;
  onCreateShiplist: (name: string) => void;
  exportTargetsToCSV: () => void;
  orderBy: string;
  setOrderBy: Dispatch<SetStateAction<string>>;
  orderAscending: boolean;
  setOrderAscending: Dispatch<SetStateAction<boolean>>;
  isBulkTracksActive: boolean;
  isSelectingBulkTracks: boolean;
  bulkTrackedMMSIs: string[];
  bulkTrackedVesselNames: string[];
  bulkTracksHours: number;
  setIsBulkTracksActive: Dispatch<SetStateAction<boolean>>;
  setIsSelectingBulkTracks: Dispatch<SetStateAction<boolean>>;
  setBulkTrackedMMSIs: Dispatch<SetStateAction<string[]>>;
  setBulkTrackedVesselNames: Dispatch<SetStateAction<string[]>>;
  setBulkTracksHours: Dispatch<SetStateAction<number>>;
}

const TargetsOnMapContext = createContext<TargetsOnMapContextProps | null>(
  null,
);

export const calculatePaginationValues = (
  currentPage: number,
  totalItems: number,
  itemsPerPage: number,
) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate remainder for the last page
  const remainder = totalItems % itemsPerPage;
  const lastPageSize = remainder === 0 ? itemsPerPage : remainder;

  const isLastPage = currentPage === totalPages;
  // If we're on the last page, use the remainder size, otherwise use full page size
  const currentPageSize = isLastPage ? lastPageSize : itemsPerPage;

  // Calculate start index based on whether we're on the last page
  const startIndex = (currentPage - 1) * itemsPerPage;

  return {
    startIndex,
    pageSize: currentPageSize,
    totalPages,
    isLastPage,
  };
};

export const usePagination = (
  currentPage: number,
  totalItems: number,
  itemsPerPage: number,
) => {
  return useMemo(
    () => calculatePaginationValues(currentPage, totalItems, itemsPerPage),
    [currentPage, totalItems, itemsPerPage],
  );
};

export const TargetsOnMapProvider: React.FC<ShapeProviderProps> = ({
  children,
}) => {
  const [targets, setTargets] = useState<TargetFromTargetList[]>([]);
  const [orderBy, setOrderBy] = useState<string>('name');
  const [orderAscending, setOrderAscending] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(100);
  const [page, setPage] = useState<number>(1);
  const [retrievalTime, setRetrievalTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isBulkTracksActive, setIsBulkTracksActive] = useState<boolean>(false);
  const [isSelectingBulkTracks, setIsSelectingBulkTracks] =
    useState<boolean>(false);
  const [bulkTrackedMMSIs, setBulkTrackedMMSIs] = useState<string[]>([]);
  const [bulkTrackedVesselNames, setBulkTrackedVesselNames] = useState<
    string[]
  >([]);
  const [bulkTracksHours, setBulkTracksHours] = useState<number>(4);

  const { messages } = useLocalization();
  const { toast } = useToast();

  const { simpleFilters, customFilters } = useFilterContext();
  const previousFilters = useRef({
    simple: simpleFilters,
    custom: customFilters,
  });

  const { mutate: mutateShiplist } = useAddShiplist();

  const { createdAt } = useMapOverlay();
  const previousCreatedAt = useRef(new Date());
  const previousCoordinates = useRef({
    minLat: 0,
    maxLat: 0,
    minLon: 0,
    maxLon: 0,
  });

  const { olSession } = useSession();
  const { map, displayProjection, dataProjection } = useOpenLayersContext();

  const { centerComponent } = useMapPanelContext();

  const getTargets = useCallback(
    async (requestedPerPage: number | null = null) => {
      const dimensions = getDimensionsFromMap(
        map,
        displayProjection,
        dataProjection,
      );
      if (!dimensions) {
        return;
      }
      setIsLoading(true);
      const { northEast, southWest } = dimensions;

      // If requestedPerPage is provided (e.g., fetching all for popup),
      // use it directly without pagination calculations
      let startIndex: number;
      let pageSize: number;

      if (requestedPerPage !== null) {
        // Fetch all from the beginning
        startIndex = 0;
        pageSize = requestedPerPage;
      } else {
        // Normal paginated fetch
        const paginationValues = calculatePaginationValues(
          page,
          total,
          perPage,
        );
        startIndex = paginationValues.startIndex;
        pageSize = paginationValues.pageSize;
      }

      const props = {
        minLat: southWest.lat,
        maxLat: northEast.lat,
        minLon: southWest.lng,
        maxLon: northEast.lng,
        targetsPerPage: pageSize,
        startIndex: startIndex,
        orderBy: orderBy,
        ascending: orderAscending,
      };

      previousCoordinates.current = {
        minLat: southWest.lat,
        maxLat: northEast.lat,
        minLon: southWest.lng,
        maxLon: northEast.lng,
      };

      try {
        return await ignoreSessionUnavailable(getTargetList(olSession, props));
      } finally {
        setIsLoading(false);
      }
    },
    [
      map,
      displayProjection,
      olSession,
      orderAscending,
      orderBy,
      page,
      perPage,
      total,
      dataProjection,
    ],
  );

  const onGetTargets = useCallback(
    async (_requestedPerPage: number | null = null) => {
      // If a targets popup is active, notify it that we're loading
      if (isTargetsPopupActive()) {
        MainWindowActions.sendDataUpdate(
          DataType.TARGETS,
          null,
          undefined,
          true,
        );
      }

      const targetData = await getTargets();
      if (!targetData) {
        return [];
      }
      setTargets(targetData.targets);
      setTotal(targetData.totalShips);
      setRetrievalTime(targetData.timeStamp);

      // Broadcast to any open popup windows
      // If a targets popup is active, fetch ALL targets for accurate charts
      if (isTargetsPopupActive() && targetData.totalShips > 0) {
        // Fetch all targets for the popup (not paginated)
        const allTargetsData = await getTargets(targetData.totalShips);
        if (allTargetsData) {
          MainWindowActions.sendDataUpdate(DataType.TARGETS, {
            targets: allTargetsData.targets,
            total: allTargetsData.totalShips,
            retrievalTime: allTargetsData.timeStamp,
            bounds: previousCoordinates.current,
          });
        }
      }
      return targetData.targets;
    },
    [getTargets],
  );

  const onCreateShiplist = useCallback(
    async (name: string) => {
      setIsSubmitting(true);
      toast({
        title: messages.genericToastTitleInfo,
        description: messages.targetsOnMapStartingToCreateShiplist,
        variant: 'info',
      });

      // We set the total to the perPage to get all the targets
      const targetData = await getTargets(total);

      if (!targetData) {
        setIsSubmitting(false);
        toast({
          title: messages.genericToastTitleError,
          description: messages.targetsOnMapFailedToCreateShiplist,
          variant: 'destructive',
        });
        return;
      }

      const shipListToSave = buildShiplistFromTargets(name, targetData.targets);

      const extendedCallbacks = shiplistCallbacks(
        toast,
        messages,
        'create',
        setIsSubmitting,
      );

      mutateShiplist({ shiplist: shipListToSave }, extendedCallbacks);
    },
    [getTargets, messages, mutateShiplist, toast, total],
  );

  const exportTargetsToCSV = async () => {
    try {
      setIsSubmitting(true);
      toast({
        title: messages.genericToastTitleInfo,
        description: messages.targetsOnMapStartingToExportCsv,
        variant: 'info',
      });

      const targetData = await getTargets(total);

      if (!targetData) {
        setIsSubmitting(false);
        toast({
          title: messages.genericToastTitleError,
          description: messages.targetsOnMapFailedToExportCsv,
          variant: 'destructive',
        });
        return;
      }

      // Define all headers from the interface
      const headers = [
        // Basic vessel information
        'name',
        'mmsi',
        'imo',
        'callSign',
        'country',

        // Vessel characteristics
        'type',
        'shipType',
        'length',
        'width',
        'draught',

        // Navigation & status
        'navStatus',
        'sourceType',
        'heading',
        'cog', // Course Over Ground
        'sog', // Speed Over Ground
        'rot', // Rate of Turn
        'posAccuracy',

        // Current position
        'lat',
        'lon',
        'positionLat',
        'positionLon',

        // Trip information
        'destination',
        'eta',
        'cargoType',

        // Historical AIS positions
        'lastAisReport',
        'lastAisPositionLat',
        'lastAisPositionLon',

        // Historical AIS satellite positions
        'lastAisSatreport',
        'lastAisSatpositionLat',
        'lastAisSatpositionLon',

        // Historical AIS vessel positions
        'lastAisVesselreport',
        'lastAisVesselpositionLat',
        'lastAisVesselpositionLon',

        // Historical LRIT positions
        'lastLritReport',
        'lastLritPositionLat',
        'lastLritPositionLon',

        // Historical radar positions
        'lastRadarReport',
        'lastRadarPositionLat',
        'lastRadarPositionLon',
      ];

      // Helper function to escape and format CSV values
      const formatCSVValue = (value: any): string => {
        if (value === null || value === undefined) {
          return '';
        }

        // Convert to string and handle special characters
        const stringValue = String(value);
        if (
          stringValue.includes(',') ||
          stringValue.includes('"') ||
          stringValue.includes('\n')
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Create CSV content
      const csvContent = [
        // Header row
        headers.join(','),
        // Data rows
        ...targetData.targets.map((target) =>
          headers
            .map((header) =>
              formatCSVValue(target[header as keyof TargetFromTargetList]),
            )
            .join(','),
        ),
      ].join('\n');

      // Create blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Set up download
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `targets_export_${timestamp}.csv`);
      link.style.visibility = 'hidden';

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsSubmitting(false);
    } catch {
      setIsSubmitting(false);
      toast({
        title: messages.genericToastTitleError,
        description: messages.targetsOnMapFailedToExportCsv,
        variant: 'destructive',
      });
    }
  };

  // This useEffect will run every time we get a new overlay
  useEffect(() => {
    const updateTargetsOnMapData = async () => {
      if (createdAt && createdAt !== previousCreatedAt.current) {
        previousCreatedAt.current = createdAt;
        previousFilters.current = {
          simple: simpleFilters,
          custom: customFilters,
        };
        setPage(1);
        await onGetTargets();
      }
    };
    // Run if centerComponent is TARGETSONMAP OR if a targets popup is active
    const shouldUpdate =
      centerComponent === CENTERCOMPONENTS.TARGETSONMAP ||
      isTargetsPopupActive();
    if (!shouldUpdate) return;

    const filtersChanged =
      simpleFilters !== previousFilters.current.simple ||
      customFilters !== previousFilters.current.custom;

    // In case the filters have changed, we need to update the targets even though the map hasn't moved
    //  we wait a second to make sure the overlay has also been updated. It's a bit of hack.
    if (filtersChanged) {
      setTimeout(() => {
        updateTargetsOnMapData();
      }, 1000);
      return;
    }

    const dimensions = getDimensionsFromMap(
      map,
      displayProjection,
      dataProjection,
    );
    if (!dimensions) return;

    if (
      previousCoordinates.current.minLat === dimensions.southWest.lat &&
      previousCoordinates.current.maxLat === dimensions.northEast.lat &&
      previousCoordinates.current.minLon === dimensions.southWest.lng &&
      previousCoordinates.current.maxLon === dimensions.northEast.lng
    )
      return;

    updateTargetsOnMapData();
  }, [
    centerComponent,
    createdAt,
    map,
    displayProjection,
    onGetTargets,
    dataProjection,
  ]);

  const value = {
    targets,
    onGetTargets,
    total,
    perPage,
    setPerPage,
    retrievalTime,
    page,
    setPage,
    isLoading,
    isSubmitting,
    onCreateShiplist,
    exportTargetsToCSV,
    orderBy,
    setOrderBy,
    orderAscending,
    setOrderAscending,
    isBulkTracksActive,
    isSelectingBulkTracks,
    bulkTrackedMMSIs,
    bulkTrackedVesselNames,
    bulkTracksHours,
    setIsBulkTracksActive,
    setIsSelectingBulkTracks,
    setBulkTrackedMMSIs,
    setBulkTrackedVesselNames,
    setBulkTracksHours,
  };

  return (
    <TargetsOnMapContext.Provider value={value}>
      {children}
    </TargetsOnMapContext.Provider>
  );
};

export const useTargetsOnMap = () => {
  const context = useContext(TargetsOnMapContext);
  if (!context) {
    throw new Error(
      'useTargetsOnMap must be used within a TargetsOnMapProvider',
    );
  }
  return context;
};

interface ShapeProviderProps {
  children: ReactNode;
}
